import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getGameMeta } from '@shared/data/gamesMeta';
import {
  setFirstGameStepIndex,
  toggleFirstGameStepCompleted,
} from '@shared/state/gamePrefs';
import { getPwaGameBundle } from '../content/gameBundle';
import { useGamePrefs } from '../state/GamePrefsProvider';
import {
  PolishSpeechBottomInsetProvider,
  PolishSpeechReaderPanel,
} from '../components/reading/PolishSpeechReaderPanel';

function FirstGameBody() {
  const navigate = useNavigate();
  const { gameId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const game = getGameMeta(gameId);
  const { ready, getPrefs, patchPrefs } = useGamePrefs();
  const bundle = useMemo(() => getPwaGameBundle(gameId), [gameId]);
  const steps = bundle.firstGameSteps;
  const prefs = getPrefs(gameId);

  const stepFromUrl = Number(searchParams.get('step') ?? '');
  const currentStepIndex = Math.min(
    Math.max(
      Number.isFinite(stepFromUrl)
        ? stepFromUrl
        : prefs.firstGameStepIndex,
      0
    ),
    Math.max(steps.length - 1, 0)
  );
  const currentStep = steps[currentStepIndex];
  const totalSteps = steps.length;
  const stepKey = currentStep ? String(currentStep.id) : '';
  const isCompleted = prefs.firstGameCompletedStepIds.includes(stepKey);
  const progress =
    totalSteps > 0 ? ((currentStepIndex + 1) / totalSteps) * 100 : 0;

  useEffect(() => {
    if (!ready || !currentStep) return;
    if (prefs.firstGameStepIndex !== currentStepIndex) {
      patchPrefs(gameId, setFirstGameStepIndex(prefs, currentStepIndex));
    }
  }, [
    ready,
    currentStep,
    currentStepIndex,
    gameId,
    patchPrefs,
    prefs,
  ]);

  const goTo = (index: number) => {
    const next = Math.min(Math.max(index, 0), Math.max(totalSteps - 1, 0));
    setSearchParams({ step: String(next) });
    patchPrefs(gameId, setFirstGameStepIndex(getPrefs(gameId), next));
  };

  if (!game) {
    return (
      <div className="empty">
        <h1>Nie znaleziono gry</h1>
        <Link to="/">Wróć</Link>
      </div>
    );
  }

  if (!currentStep || totalSteps === 0) {
    return (
      <div className="empty">
        <h1>Pierwsza gra</h1>
        <p className="meta">Brak kroków pierwszej gry dla tej pozycji.</p>
        <Link to={`/game/${gameId}`}>Wróć do gry</Link>
      </div>
    );
  }

  const speechChunks = [
    currentStep.title,
    ...currentStep.content,
    currentStep.why ? `Dlaczego: ${currentStep.why}` : '',
    currentStep.commonMistake
      ? `Uwaga: ${currentStep.commonMistake}`
      : '',
  ].filter(Boolean);

  return (
    <section
      className="panel first-game-screen"
      data-testid="first-game-screen"
    >
      <p className="eyebrow">{game.title}</p>
      <h1>Pierwsza gra</h1>
      <div className="progress-track" aria-hidden>
        <div className="progress-bar" style={{ width: `${progress}%` }} />
      </div>
      <p className="meta" data-testid="first-game-step-indicator">
        Krok {currentStepIndex + 1} z {totalSteps}
      </p>

      <h2 data-testid="first-game-step-title">{currentStep.title}</h2>
      <ol className="block-list">
        {currentStep.content.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ol>
      {currentStep.why ? (
        <div className="block-tip">Dlaczego: {currentStep.why}</div>
      ) : null}
      {currentStep.commonMistake ? (
        <div className="block-warning">
          Częsty błąd: {currentStep.commonMistake}
        </div>
      ) : null}

      <label className="block-checklist" style={{ marginTop: 16 }}>
        <input
          type="checkbox"
          data-testid="first-game-complete-toggle"
          checked={isCompleted}
          onChange={(event) =>
            patchPrefs(
              gameId,
              toggleFirstGameStepCompleted(
                getPrefs(gameId),
                stepKey,
                event.target.checked
              )
            )
          }
        />
        <span>Oznacz krok jako wykonany</span>
      </label>

      {currentStep.chapterIndex != null ? (
        <button
          type="button"
          className="ghost-btn"
          style={{ marginTop: 12 }}
          onClick={() =>
            navigate(`/game/${gameId}/manual/${currentStep.chapterIndex}`)
          }
        >
          Czytaj pełną zasadę
        </button>
      ) : null}

      <div className="chapter-nav" style={{ position: 'relative' }}>
        <button
          type="button"
          className="nav-btn"
          data-testid="first-game-back"
          disabled={currentStepIndex <= 0}
          onClick={() => goTo(currentStepIndex - 1)}
        >
          Wstecz
        </button>
        {currentStepIndex >= totalSteps - 1 ? (
          <button
            type="button"
            className="nav-btn"
            data-testid="first-game-finish"
            onClick={() => navigate(`/game/${gameId}`)}
          >
            Zakończ
          </button>
        ) : (
          <button
            type="button"
            className="nav-btn"
            data-testid="first-game-next"
            onClick={() => goTo(currentStepIndex + 1)}
          >
            Dalej
          </button>
        )}
      </div>

      <button
        type="button"
        className="ghost-btn"
        data-testid="first-game-reset"
        style={{ marginTop: 12 }}
        onClick={() => {
          patchPrefs(gameId, {
            firstGameStepIndex: 0,
            firstGameCompletedStepIds: [],
          });
          setSearchParams({ step: '0' });
        }}
      >
        Resetuj pierwszą grę
      </button>

      <PolishSpeechReaderPanel
        gameId={gameId}
        sourceKey={`${gameId}:first-game:${currentStepIndex}`}
        chunks={speechChunks}
      />
    </section>
  );
}

export function FirstGameScreen() {
  return (
    <PolishSpeechBottomInsetProvider>
      <FirstGameBody />
    </PolishSpeechBottomInsetProvider>
  );
}
